import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Box,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckInIcon,
  EmojiEvents as GoalIcon,
  Description as JournalIcon,
  Chat as MessageIcon,
  Assessment as AssessmentIcon,
  Person as ClientIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

function RecentActivityWidget({ activities, onClientClick }) {
  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Recent Activity
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            No recent activity from clients
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getActivityIcon = (eventType) => {
    if (eventType.includes('CHECKIN')) return <CheckInIcon />;
    if (eventType.includes('GOAL')) return <GoalIcon />;
    if (eventType.includes('JOURNAL')) return <JournalIcon />;
    if (eventType.includes('MESSAGE')) return <MessageIcon />;
    if (eventType.includes('ASSESSMENT')) return <AssessmentIcon />;
    return <ClientIcon />;
  };

  const getActivityColor = (eventType) => {
    if (eventType.includes('CHECKIN')) return '#2e7d32';
    if (eventType.includes('GOAL')) return '#ed6c02';
    if (eventType.includes('JOURNAL')) return '#1976d2';
    if (eventType.includes('MESSAGE')) return '#9c27b0';
    if (eventType.includes('ASSESSMENT')) return '#d32f2f';
    return '#757575';
  };

  const getActivityLabel = (eventType) => {
    if (eventType.includes('CHECKIN')) return 'Check-in';
    if (eventType.includes('GOAL')) return 'Goal';
    if (eventType.includes('JOURNAL')) return 'Journal';
    if (eventType.includes('MESSAGE')) return 'Message';
    if (eventType.includes('ASSESSMENT')) return 'Assessment';
    return 'Activity';
  };

  const formatActivityDescription = (activity) => {
    const metadata = activity.metadata || {};

    if (activity.eventType.includes('CHECKIN_CREATED')) {
      return `Mood: ${metadata.mood}/10${metadata.anxietyLevel ? `, Anxiety: ${metadata.anxietyLevel}/10` : ''}`;
    }

    if (activity.eventType.includes('GOAL_CREATED')) {
      return metadata.goalTitle || 'New goal created';
    }

    if (activity.eventType.includes('GOAL_COMPLETED')) {
      return `Completed: ${metadata.goalTitle || 'goal'}`;
    }

    if (activity.eventType.includes('JOURNAL_CREATED')) {
      return metadata.journalTitle || 'New journal entry';
    }

    if (activity.eventType.includes('MESSAGE_SENT')) {
      return 'New message sent';
    }

    if (activity.eventType.includes('ASSESSMENT_COMPLETED')) {
      return `Completed: ${metadata.assessmentType || 'assessment'}`;
    }

    return activity.eventType.replace(/_/g, ' ').toLowerCase();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Recent Activity
        </Typography>

        <List disablePadding>
          {activities.slice(0, 10).map((activity, index) => (
            <React.Fragment key={activity._id || index}>
              {index > 0 && <Divider variant="inset" component="li" />}
              <ListItem
                button
                onClick={() => activity.clientId && onClientClick(activity.clientId)}
                sx={{ py: 1.5 }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      backgroundColor: getActivityColor(activity.eventType),
                      width: 36,
                      height: 36
                    }}
                  >
                    {getActivityIcon(activity.eventType)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {activity.clientName}
                      </Typography>
                      <Chip
                        label={getActivityLabel(activity.eventType)}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: getActivityColor(activity.eventType),
                          color: 'white'
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatActivityDescription(activity)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>

        {activities.length > 10 && (
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            display="block"
            mt={2}
          >
            Showing 10 of {activities.length} recent activities
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default RecentActivityWidget;
